import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../environments/environment";

/** Which entity a module's Target/Indexes/Behaviour config belongs to. */
export type ConfigScope = "site" | "subcategory";

/**
 * Module Target / Indexes / Behaviour & Finetuning for a module inside a **site** or a
 * **sub-category**. Both scopes share this contract shape but hit different endpoints
 * and different tables (`tbl_site_*` vs `subCategory_*`).
 *
 * Deliberately separate from GenericLibraryService, which serves the System-Admin
 * module library page; that page is in QA and must not be perturbed. The three are
 * expected to be merged once it clears. See MODULE_CONFIG_TABS_BACKEND_SPEC.md.
 */
@Injectable({ providedIn: "root" })
export class ModuleConfigTabsService {
  constructor(private http: HttpClient) {}

  private post(scope: ConfigScope, siteAction: string, subCategoryAction: string, request: any) {
    const action = scope === "site" ? siteAction : subCategoryAction;
    return this.http.post(`${environment.nodeUrl1}${action}`, request).toPromise();
  }

  getModuleTarget(scope: ConfigScope, request) {
    return this.post(scope, "getSiteModuleTarget", "getSubCategoryModuleTarget", request);
  }

  saveModuleTarget(scope: ConfigScope, request) {
    return this.post(scope, "saveSiteModuleTarget", "saveSubCategoryModuleTarget", request);
  }

  getModuleIndexes(scope: ConfigScope, request) {
    return this.post(scope, "getSiteModuleIndexes", "getSubCategoryModuleIndexes", request);
  }

  saveModuleIndexes(scope: ConfigScope, request) {
    return this.post(scope, "saveSiteModuleIndexes", "saveSubCategoryModuleIndexes", request);
  }

  getBehaviourFinetuning(scope: ConfigScope, request) {
    return this.post(scope, "getSiteBehaviourFinetuning", "getSubCategoryBehaviourFinetuning", request);
  }

  saveBehaviourFinetuning(scope: ConfigScope, request) {
    return this.post(scope, "saveSiteBehaviourFinetuning", "saveSubCategoryBehaviourFinetuning", request);
  }

  deleteAspect(scope: ConfigScope, request) {
    return this.post(scope, "deleteSiteAspect", "deleteSubCategoryAspect", request);
  }

  /**
   * The System-Admin library's indexes for a module, read only, so this dialog can warn
   * when the library defines indexes the site/sub-category has not configured.
   * Same endpoint the module library page uses — see MODULE_TARGET_BACKEND_SPEC.md.
   */
  getLibraryModuleIndexes(moduleId: string | number) {
    return this.http
      .post(`${environment.nodeUrl1}getModuleIndexes`, { module_id: moduleId })
      .toPromise();
  }

  /**
   * The library's target definition for a module. Read only, and used for one thing:
   * seeding a **draft** — a module being configured on a sub-category that does not
   * exist yet, so there is no scope row to read. See `draft` on the tabs component.
   */
  getLibraryModuleTarget(moduleId: string | number) {
    return this.http
      .post(`${environment.nodeUrl1}getModuleTarget`, { module_id: moduleId })
      .toPromise();
  }

  /** The library's behaviour/finetuning text for a module. Draft seeding only. */
  getLibraryModuleBehaviour(moduleId: string | number) {
    return this.http
      .post(`${environment.nodeUrl1}getModuleBehaviour`, { module_id: moduleId })
      .toPromise();
  }
}
